import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal';
import Button from '../../common/FormFields/Button';
import { createReservation } from '../../../services/fetch';

const PREFIX = 'configuration.settings.bookAppointmentModal';

const ForceConfirmModal = ({
  isOpen,
  failedChecks = [],
  details = {},
  overrideAllowed = false,
  userId,
  timeslotId,
  bloodBankId,
  bookingStart,
  onClose,
  onSuccess,
  onError,
  onForceBookCustom,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const buildViolations = () => {
    const violations = [];

    (failedChecks || []).forEach((check) => {
      const detail = details?.[check] || {};
      const itemOverridable = detail.overrideAllowed === true;

      switch (check) {
        case 'earliestDate': {
          const dateStr = detail.value || '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckEarliestDate`, { date: dateStr }),
            overridable: itemOverridable,
          });
          break;
        }

        case 'donorQuarantine': {
          const raw = detail.value;
          const dateStr = raw
            ? new Date(raw).toLocaleDateString('no-NO', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckQuarantine`, { date: dateStr }),
            overridable: itemOverridable,
          });
          break;
        }

        case 'aboRh': {
          const mismatch = detail.mismatchType;
          let msgKey;
          if (mismatch === 'both') {
            msgKey = `${PREFIX}.forceBookCheckAboRhdMismatch`;
          } else if (mismatch === 'rhd') {
            msgKey = `${PREFIX}.forceBookCheckRhdMismatch`;
          } else {
            msgKey = `${PREFIX}.forceBookCheckAboMismatch`;
          }
          violations.push({
            key: check,
            message: t(msgKey),
            overridable: itemOverridable,
          });
          break;
        }

        case 'isEligible': {
          const reason = detail.reason;
          const msgKey =
            reason === 'missingEligibilityEntry'
              ? `${PREFIX}.forceBookCheckMissingEligibility`
              : `${PREFIX}.forceBookCheckNotEligible`;
          violations.push({
            key: check,
            message: t(msgKey),
            overridable: itemOverridable,
          });
          break;
        }

        case 'appointmentTypeMismatch': {
          const originalType = detail.originalType || '';
          const selectedType = detail.selectedType || '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckAppointmentTypeMismatch`, { originalType, selectedType }),
            overridable: true,
          });
          break;
        }

        case 'timeslotRange': {
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckTimeslotRange`),
            overridable: false,
          });
          break;
        }

        case 'existingAppointment': {
          const raw = detail.value;
          const dateStr = raw
            ? new Date(raw).toLocaleDateString('no-NO', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : '';
          const timeStr = raw
            ? new Date(raw).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })
            : '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckExistingAppointment`, { date: dateStr, time: timeStr }),
            overridable: true,
          });
          break;
        }

        default:
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckUnknown`, { check }),
            overridable: itemOverridable,
          });
      }
    });

    return violations;
  };

  const violations = buildViolations();

  const handleForceBook = async () => {
    setIsLoading(true);
    try {
      if (onForceBookCustom) {
        await onForceBookCustom();
      } else {
        await createReservation(
          `/reservations/bloodbank/${bloodBankId}`,
          bookingStart,
          timeslotId,
          userId,
          true,
        );
      }
      setIsLoading(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setIsLoading(false);
      onError?.(err);
      onClose();
    }
  };

  return (
    <Modal isModalOpen={isOpen} handleModalClose={onClose}>
      <div className="modal-header">
        <h2 className="modal-title">
          {t(`${PREFIX}.forceBookTitle`)}
        </h2>
        <button className="modal-close" onClick={onClose}>
          <span className="icon-close" />
        </button>
      </div>

      <div className="modal-body modal-content">
        <p className="modal-msg" style={{ marginBottom: 12 }}>
          {t(`${PREFIX}.forceBookBody`)}
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {violations.map((v) => (
            <li
              key={v.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '6px 0',
                borderBottom: '1px solid #eee',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  minWidth: 10,
                  borderRadius: '50%',
                  marginTop: 3,
                  backgroundColor: v.overridable ? '#f5a623' : '#d0021b',
                }}
              />
              <span style={{ flex: 1 }}>
                <span>{v.message}</span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 11,
                    marginTop: 2,
                    color: v.overridable ? '#b27c00' : '#9b0015',
                    fontWeight: 600,
                  }}
                >
                  {v.overridable
                    ? t(`${PREFIX}.forceBookOverridable`)
                    : t(`${PREFIX}.forceBookNotOverridable`)}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {!overrideAllowed && (
          <p
            style={{
              marginTop: 14,
              padding: '8px 12px',
              borderRadius: 4,
              backgroundColor: '#fdecea',
              color: '#611a15',
              fontSize: 13,
            }}
          >
            {t(`${PREFIX}.forceBookNotAllowed`)}
          </p>
        )}
      </div>

      <div className="modal-footer light w-100">
        <div className="inputs-group">
          <div className="inputs-group-item">
            <Button
              btnClassName="m-10"
              type="button"
              text={t('common.cancel')}
              onClick={onClose}
              disabled={isLoading}
            />
          </div>
          <div className="inputs-group-item">
            <Button
              btnClassName="btn-primary"
              type="button"
              text={t(`${PREFIX}.forceBook`)}
              onClick={handleForceBook}
              disabled={isLoading || !overrideAllowed}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ForceConfirmModal;